var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable complexity */
/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 *
 */
import { getWellKnown } from '../endpoints/well-known';
import { AuthSdkError } from '../../errors';
import { clone } from '../../util';
import { getDefaultTokenParams } from './defaultTokenParams';
import { DEFAULT_CODE_CHALLENGE_METHOD } from '../../constants';
import PKCE from './pkce';
export function assertPKCESupport(sdk) {
    if (!sdk.features.isPKCESupported()) {
        var errorMessage = 'PKCE requires a modern browser with encryption support running in a secure context.';
        if (!sdk.features.isHTTPS()) {
            // eslint-disable-next-line max-len
            errorMessage += '\nThe current page is not being served with HTTPS protocol. PKCE requires secure HTTPS protocol.';
        }
        if (!sdk.features.hasTextEncoder()) {
            // eslint-disable-next-line max-len
            errorMessage += '\n"TextEncoder" is not defined. To use PKCE, you may need to include a polyfill/shim for this browser.';
        }
        throw new AuthSdkError(errorMessage);
    }
}
export function validateCodeChallengeMethod(sdk, codeChallengeMethod) {
    return __awaiter(this, void 0, void 0, function* () {
        // set default code challenge method, if none provided
        codeChallengeMethod = codeChallengeMethod || sdk.options.codeChallengeMethod || DEFAULT_CODE_CHALLENGE_METHOD;
        // validate against .well-known/openid-configuration
        const wellKnownResponse = yield getWellKnown(sdk);
        var methods = wellKnownResponse['code_challenge_methods_supported'] || [];
        if (methods.indexOf(codeChallengeMethod) === -1) {
            throw new AuthSdkError('Invalid code_challenge_method');
        }
        return codeChallengeMethod;
    });
}
export function preparePKCE(sdk, tokenParams) {
    return __awaiter(this, void 0, void 0, function* () {
        let { codeVerifier, codeChallenge, codeChallengeMethod } = tokenParams;
        // PKCE calculations can be avoided by passing a codeChallenge
        codeChallenge = codeChallenge || sdk.options.codeChallenge;
        if (!codeChallenge) {
            assertPKCESupport(sdk);
            codeVerifier = codeVerifier || PKCE.generateVerifier();
            codeChallenge = yield PKCE.computeChallenge(codeVerifier);
        }
        codeChallengeMethod = yield validateCodeChallengeMethod(sdk, codeChallengeMethod);
        // Clone/copy the params. Set PKCE values
        tokenParams = Object.assign(Object.assign({}, tokenParams), { responseType: 'code', // responseType is forced
            codeVerifier,
            codeChallenge,
            codeChallengeMethod });
        return tokenParams;
    });
}
// Prepares params for a call to /authorize or /token
export function prepareTokenParams(sdk, tokenParams = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        // build params using defaults + options
        const defaults = getDefaultTokenParams(sdk);
        tokenParams = Object.assign({}, defaults, clone(tokenParams));
        if (tokenParams.pkce === false) {
            // Implicit flow or authorization_code without PKCE
            return tokenParams;
        }
        return preparePKCE(sdk, tokenParams);
    });
}
